/**
 * useSession — shared TanStack Query hook.
 *
 * Fetches GET /api/auth/me and returns the current session member.
 * Used by admin-gated hooks (useAiUsage) to check role before fetching.
 *
 * queryKey: ['session'] — invalidated on logout (Phase 1 auth flow).
 */
import { useQuery } from '@tanstack/react-query';

export interface SessionMember {
  id: string;
  role: 'admin' | 'teen' | 'child';
  short_name: string;
  color_slot: string;
  /** Personfargen slik /api/auth/me faktisk sender den (`m-terracotta` …). */
  color_token?: string;
  name?: string;
  /** Innloggingsnavnet (#140). `null` for medlemmer uten brukernavn ennå. */
  username?: string | null;
  /** #143: arvet husstandspassord — må velge eget før appen kan brukes. */
  must_change_password?: boolean;
  /** FF-11 (#179): adminen har ikke fullført oppsettet av familien. */
  family_setup_pending?: boolean;
}

export const SESSION_KEY = ['session'] as const;

export function useSession() {
  return useQuery<{ member: SessionMember } | null>({
    queryKey: SESSION_KEY,
    queryFn: async () => {
      const res = await globalThis.fetch('/api/auth/me', { credentials: 'same-origin' });
      if (!res.ok) return null;
      return (await res.json()) as { member: SessionMember };
    },
    staleTime: 30_000,
  });
}
